import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useGetProductList } from "@/api/client/product";
import { getPopularProducts } from "@/api/server/product";
import { SortModal } from "@/components/actions";
import SortFilter from "@/components/actions/SortFilter";
import ProductCard from "@/components/data-display/card/product";
import CategoryHeading from "@/components/data-display/categoryHeading";
import { SortIcon } from "@/components/data-display/icon";
import Loader from "@/components/data-display/loaderSvg";
import { HeroFeedback } from "@/components/feedback";
import CategoriesMenu from "@/components/navigation/categories";
import Pagination from "@/components/navigation/pagination";
import MetaData from "@/components/seo/MetaData";
import Breadcrumbs from "@/components/ui/navigation/breadcrumbs";
import useRouterPush from "@/hooks/useRouterPush";
import { getWishlistState } from "@/store/feature/wishlistSlice";
import statusCodes from "@/utils/constants/http-status-codes";
import { revalidationTime } from "@/utils/constants/server-side.constants";
import { getPageResult } from "@/utils/helpers/pageResult";
import { generateUrlToQuery } from "@/utils/helpers/queryString";
import { formatSortOptions } from "@/utils/helpers/sort";
import { updateListWithWishlistStatus } from "@/utils/helpers/wishlist";

const PopularProducts = ({ data }) => {
  const [meta, setMeta] = useState(data?.meta);
  const [products, setProducts] = useState(data?.products);
  const [isSortModal, setSortModal] = useState(false);

  const { wishlistItems } = useSelector(getWishlistState);

  const { updateQueryParams, queryParams } = useRouterPush();

  useEffect(() => {
    setMeta(data?.meta);
    setProducts(data?.products);
  }, [data]);

  useEffect(() => {
    if (!Object.keys(wishlistItems).length) return;
    setProducts(updateListWithWishlistStatus(products, wishlistItems));
  }, [Object.keys(wishlistItems).length]);

  const { isFetching: isProductLoading } = useGetProductList(
    generateUrlToQuery("", { is_popular: true, ...queryParams }),
    {
      onSuccess: (data) => {
        setMeta(data?.meta);

        if (Object.keys(wishlistItems).length) {
          setProducts(
            updateListWithWishlistStatus(data?.products, wishlistItems)
          );
        } else {
          setProducts(data?.products);
        }
      },
      enabled: Object.keys(queryParams).length > 0,
    }
  );

  return (
    <>
      <MetaData {...data.metadata} schema={data.schema} />
      <div className="container pb-[1rem]">
        {isProductLoading && <Loader />}

        <div className="my-[1.375rem]">
          <Breadcrumbs
            breadcrumbs={[
              { link: "", value: "Home" },
              { link: "", value: "Popular Products" },
            ]}
          />
        </div>

        <div className="grid grid-cols-12 gap-4 pb-5 lg:space-x-[4.375rem]">
          <aside className="hidden min-w-[12rem] pb-5 lg:col-span-2 lg:block">
            <p className="py-6 text-grayDisplay-300">All Categories</p>

            <CategoriesMenu categoryList={data?.categories} />
          </aside>

          <section className="col-span-12 lg:col-span-10">
            <div className="my-5 md:my-8">
              <CategoryHeading categoryName="Popular Products" />
            </div>

            {products?.length > 0 ? (
              <>
                <div className="mb-6 mt-3 flex items-center justify-between gap-4">
                  <div className="flex items-center space-x-[0.75rem] text-[0.938rem] leading-[1.375rem] text-grayDisplay-300 sm:block sm:space-x-0 sm:space-y-[0.625rem]">
                    {getPageResult(meta)}
                  </div>

                  <SortFilter sortOptions={data.sortables} />

                  <div className="flex h-[2.5rem] w-[9.5rem] cursor-pointer items-center justify-center rounded-[2.625rem] bg-[#EBEEF2] md:hidden">
                    <label
                      type="button"
                      onClick={() => setSortModal(true)}
                      className="flex w-full items-center justify-center gap-2 text-center text-[1.1rem] text-[#0A101A]"
                    >
                      <SortIcon />
                      <span>Sort By</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-[0.75rem] lg:grid-cols-3 lg:gap-[1.563rem] xl:grid-cols-4">
                  {products.map((product) => (
                    <ProductCard product={product} key={product.slug} />
                  ))}
                </div>

                {meta?.total > meta?.per_page && (
                  <div className="mt-[4.563rem] lg:mt-[5.938rem]">
                    <Pagination
                      initialPage={meta.current_page - 1}
                      totalPage={Math.ceil(meta.total / meta.per_page)}
                      getPaginateData={(page) =>
                        updateQueryParams({ page: page + 1 })
                      }
                    />
                  </div>
                )}
              </>
            ) : (
              <HeroFeedback
                title="No product found!"
                imageAltText="product-not-found"
                imageURL="/assets/images/no-upcoming.png"
              />
            )}
          </section>
        </div>

        <SortModal
          isOpen={isSortModal}
          onHide={() => setSortModal(false)}
          value={{ value: queryParams.sort_by }}
          sortData={formatSortOptions(data.sortables)}
          onChange={(e) => {
            updateQueryParams({ sort_by: e.target.value, page: 1 });
            setSortModal(false);
          }}
        />
      </div>
    </>
  );
};

export async function getStaticProps() {
  const props = { data: null };

  const res = await getPopularProducts();

  if (res?.status === statusCodes.OK) {
    props.data = res.data?.data;
  }

  return { props, revalidate: revalidationTime };
}

export default PopularProducts;
